import { z, ZodType } from "zod";
import {
  AdminConfigZod,
  AdminFotografoZod,
  AdminPedidoZod,
  ComisionesFZod,
  DateZod,
  FotoZod,
  SubidaZod,
  UbicacionZod,
} from "./types";
import { mustLogin, mustLoginError, server } from "./server";

const Empty = z.record(z.string(), z.never());

export const getRoutes = {
  "/buscar": {
    p: Empty,
    q: z.union([
      z.object({ foto: z.number() }),
      z.object({ "buscador[subida_id]": z.number() }),
      z.object({
        "buscador[temporada_id]": z.number(),
        "buscador[fecha]": DateZod,
        "buscador[fotografo]": z.number(),
      }),
    ]),
    r: z.object({
      galeria: z.object({
        fotos: z.array(FotoZod),
        posicion: z.number().optional().nullable(),
      }),
    }),
  },
  "/admin/subidas": {
    p: Empty,
    q: z.object({}),
    r: z.object({ subidas: z.array(SubidaZod) }),
  },
  "/admin/fotografos": {
    p: Empty,
    q: Empty,
    r: z.object({ fotografos: z.record(z.string(), AdminFotografoZod) }),
  },
  "/admin/config": { p: Empty, q: Empty, r: AdminConfigZod },
  "/admin/pedido/:id/anular": {
    p: z.object({ id: z.number() }),
    q: Empty,
    r: z.object({
      id: z.number(),
      fecha: DateZod,
      operaciones: z.array(
        z.object({
          cuenta: z.string(),
          moneda: z.string(),
          valor: z.coerce.number(),
        })
      ),
    }),
  },
  "/admin/pedido/:id": {
    p: z.object({ id: z.number() }),
    q: Empty,
    r: z.object({ pedido: AdminPedidoZod }),
  },
  "/admin/comisiones/:temporada_id": {
    p: z.object({ temporada_id: z.number() }),
    q: Empty,
    r: z.object({
      temporada: z.object({ nombre: z.string() }),
      comisiones: z.array(
        z.object({
          fotografo_id: z.number(),
          fotografo: z.string(),
          comisiones: z.coerce.number(),
          totalf: z.coerce.number(),
          pc: z.string(),
          premios: z.coerce.number(),
          pagado: z.coerce.number(),
          saldo: z.coerce.number(),
          fotosN: z.coerce.number(),
        })
      ),
    }),
  },
  "/admin/comisiones/:temporada_id/:fotografo_id": {
    p: z.object({ temporada_id: z.number(), fotografo_id: z.number() }),
    q: Empty,
    r: z.object({
      miscomisiones: ComisionesFZod,
    }),
  },
  "/admin/miscomisiones": {
    p: Empty,
    q: Empty,
    r: z.object({
      miscomisiones: ComisionesFZod,
    }),
  },
  "/admin/cuentas/saldos": {
    p: Empty,
    q: Empty,
    r: z.object({
      saldos: z.array(
        z.object({
          cuenta_id: z.number(),
          cuenta: z.string(),
          moneda: z.string(),
          V: z.coerce.number(),
          temporada_id: z.number().optional(),
        })
      ),
      cuentas: z.array(z.object({ id: z.number(), nombre: z.string() })),
    }),
  },
  "/admin/buscar": {
    p: Empty,
    q: z.union([
      z.object({ "buscador[subida_id]": z.number() }),
      z.object({
        "buscador[estricto]": z.boolean().optional(),
        "buscador[temporada_id]": z.coerce.number().optional(),
        "buscador[fecha]": z.string().optional(),
        "buscador[fotografo]": z.coerce.number().optional(),
        "buscador[lugar_id]": z.coerce.number().optional(),
      }),
    ]),
    r: z.object({ galeria: z.object({ fotos: z.array(FotoZod) }) }),
  },
  "/admin/fotografos/ubicaciones": {
    p: Empty,
    q: Empty,
    r: z.object({
      ubicaciones: z.record(z.string(), z.array(UbicacionZod)),
    }),
  },
} as const satisfies Record<string, { p: ZodType; q: ZodType; r: ZodType }>;

export const postRoutes = {
  "/admin/pedido/renotificar/:pedido_id": {
    p: z.object({ pedido_id: z.number() }),
    b: Empty,
    r: z.any(),
  },
  "/admin/pago_comision": {
    p: Empty,
    b: z.object({
      temporada_id: z.number(),
      fotografo_id: z.number(),
      cuenta_id: z.number(),
      valor: z.number(),
      moneda: z.string(),
      obs: z.string(),
    }),
    r: z.any(),
  }, // TODO
  "/pedido/:pedido_id/set_edicion/:foto_id": {
    p: z.object({ pedido_id: z.number(), foto_id: z.number() }),
    b: z.object({ edicion: z.string() }),
    r: z.object({ status: z.literal("ok") }),
  },
  "/admin/pedido/:pedido_id/set_edicion/:foto_id": {
    p: z.object({ pedido_id: z.number(), foto_id: z.number() }),
    b: z.object({ edicion: z.string() }),
    r: z.object({ status: z.literal("ok") }),
  },
  "/admin/pedido/facturar/:id": {
    p: z.object({ id: z.number() }),
    b: Empty,
    r: z.any(),
  },
  "/admin/pedido/anular/:id": {
    p: z.object({ id: z.number() }),
    b: Empty,
    r: z.any(),
  },
} as const satisfies Record<string, { p: ZodType; b: ZodType; r: ZodType }>;

type GetRoutes = typeof getRoutes;
export type GetRouteKey = keyof GetRoutes;
export type GetInputOf<Route extends GetRouteKey> = {
  p: z.infer<GetRoutes[Route]["p"]>;
  q: z.infer<GetRoutes[Route]["q"]>;
};
type GetOutputOf<Route extends GetRouteKey> = z.infer<GetRoutes[Route]["r"]>;

type PostRoutes = typeof postRoutes;
type PostRouteKey = keyof PostRoutes;
type PostInputOf<Route extends PostRouteKey> = {
  p: z.infer<PostRoutes[Route]["p"]>;
  b: z.infer<PostRoutes[Route]["b"]>;
};
type PostOutputOf<Route extends PostRouteKey> = z.infer<PostRoutes[Route]["r"]>;

export async function GET<Route extends GetRouteKey>(
  route: Route,
  { p, q }: Partial<GetInputOf<Route>> = {}
): Promise<GetOutputOf<Route>> {
  const res = await (
    await fetch(
      `${server}${applyPathParameters(route, p ?? {})}?${new URLSearchParams(Object.fromEntries(Object.entries(q ?? {}).map(([k, v]) => [k, `${v}`]))).toString()}`,
      { credentials: "include" }
    )
  ).json();

  if (typeof res === "object" && "mustlogin" in res) throw mustLoginError;

  try {
    return getRoutes[route].r.parse(res) as GetOutputOf<Route>;
  } catch (e) {
    console.log(Array.isArray(e) ? e.slice(0, 10) : e);
    throw e;
  }
}

export async function POST<Route extends PostRouteKey>(
  route: Route,
  {
    p,
    b,
  }: {
    p?: PostInputOf<Route>["p"];
    b?: PostInputOf<Route>["b"];
  } = {}
): Promise<PostOutputOf<Route>> {
  const res = await (
    await fetch(`${server}${applyPathParameters(route, p ?? {})}`, {
      credentials: "include",
      method: "POST",
      ...(b
        ? {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(b),
          }
        : {}),
    })
  ).json();

  if (mustLogin(res)) throw mustLoginError;

  return postRoutes[route].r.parse(res);
}

function applyPathParameters(
  route: string,
  params: Record<string, string | number | boolean>
) {
  return Object.entries(params).reduce(
    (path: string, [k, v]) => path.replace(`:${k}`, `${v}`),
    route
  );
}

export async function urlToDataUrl(url: string | undefined): Promise<string> {
  if (!url) return "";
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch image: ${response.status} ${response.statusText}`
    );
  }

  return blobToDataUrl(await response.blob());
}

export function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result); // "data:image/...;base64,..."
      } else {
        reject(new Error("Failed to convert blob to data URL"));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
